import { useEffect } from 'react'

import echo from '@/lib/echo'
import { useGameStore } from '@/stores/game'

interface PhasePayload {
    phase: string
    revealIndex: number
    resultStage: number
}

/**
 * Hält das Handy mit der Lehrkraft synchron.
 *
 * Drei Wege führen zum aktuellen Zustand, weil jeder einzelne ausfallen kann:
 *
 *  1. Der Broadcast - der schnelle Weg, solange die Verbindung steht.
 *  2. Die Rückkehr in den Tab. Ein gesperrtes Handy schläft, und iOS friert
 *     WebSockets im Hintergrund ein; ohne diesen Weg zeigte das Handy nach dem
 *     Entsperren den Stand von vor fünf Minuten.
 *  3. Der Wiederaufbau der Verbindung nach einem WLAN-Aussetzer.
 *
 * Der Presence-Channel wird nur beigetreten, damit die Konsole die Anwesenheit
 * sieht - empfangen wird dort nichts.
 */
export function useSessionSync(sessionId: number | null): void {
    const hydrate = useGameStore((store) => store.hydrate)
    const applyPhase = useGameStore((store) => store.applyPhase)

    useEffect(() => {
        if (sessionId === null) {
            return
        }

        const channel = echo.private(`session.${sessionId}`)

        channel.listen('.session.phase', (payload: PhasePayload) => applyPhase(payload))

        // Anwesenheit für den Warteraum der Lehrkraft.
        echo.join(`session.${sessionId}`)

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                void hydrate()
            }
        }

        const connection = echo.connector.pusher.connection
        const onConnected = () => void hydrate()

        document.addEventListener('visibilitychange', onVisible)
        connection.bind('connected', onConnected)

        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            connection.unbind('connected', onConnected)
            echo.leave(`session.${sessionId}`)
        }
    }, [sessionId, hydrate, applyPhase])
}
